import React, { useEffect, useRef, useState } from "react";
import apiService from "../services/apiService";
import { getOutlookItemContext } from "../services/outlookContext";
import TiptapEditor from "./TiptapEditor";

/* global Office */

const MAX_RESPONSE_INPUT_LENGTH = 4000;

const escapeHtml = (value) => String(value || "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const plainTextToHtml = (text) => {
  const paragraphs = String(text || "")
    .trim()
    .split(/\n\s*\n+/)
    .filter(Boolean);

  if (!paragraphs.length) {
    return "<p></p>";
  }

  return paragraphs
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, "<br />")}</p>`)
    .join("");
};

const htmlToPlainText = (html) => {
  const container = document.createElement("div");
  container.innerHTML = html || "";
  return container.innerText || container.textContent || "";
};

const isOfficeSucceeded = (result) => {
  if (result?.status === "succeeded") {
    return true;
  }

  try {
    return result?.status === Office.AsyncResultStatus?.Succeeded;
  } catch {
    return false;
  }
};

const readOutlookBody = (item) => new Promise((resolve) => {
  if (!item?.body?.getAsync) {
    resolve("");
    return;
  }

  item.body.getAsync("text", (result) => {
    resolve(isOfficeSucceeded(result) ? result.value || "" : "");
  });
});

const copyTextToClipboard = async (text) => {
  // 1. Try synchronous execCommand first (most reliable in Outlook Add-in iframes)
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textarea);
    if (successful) {
      return true;
    }
  } catch (execErr) {
    console.warn("execCommand copy fallback failed:", execErr);
  }

  // 2. Fallback to navigator.clipboard.writeText
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  throw new Error("Clipboard copy not supported in this environment");
};

const EmailReplyDraft = ({
  currentMessageId,
  mailboxContext,
  isOpen: controlledIsOpen,
  onOpenChange,
  analyseAttachments = false,
  onToggleAnalyseAttachments,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const setOpen = (nextOpen) => {
    if (!isControlled) {
      setInternalIsOpen(nextOpen);
    }
    if (onOpenChange) {
      onOpenChange(nextOpen);
    }
  };

  const [responseInput, setResponseInput] = useState("");
  const [draftHtml, setDraftHtml] = useState("");
  const [draftStatus, setDraftStatus] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const currentMessageIdRef = useRef(currentMessageId || "");
  currentMessageIdRef.current = currentMessageId || "";

  useEffect(() => {
    setOpen(false);
    setResponseInput("");
    setDraftHtml("");
    setDraftStatus("");
    setGenerating(false);
    setError("");
    setCopied(false);
  }, [currentMessageId]);

  const getCurrentItem = () => {
    try {
      return Office.context?.mailbox?.item || null;
    } catch {
      return null;
    }
  };

  const handleGenerate = async () => {
    const item = getCurrentItem();
    if (!item) {
      setError("Please select an email before generating a reply.");
      return;
    }

    setGenerating(true);
    setError("");
    setCopied(false);
    let requestMessageId = "";

    try {
      const metadata = await getOutlookItemContext(item);
      requestMessageId = metadata.messageId || currentMessageId || mailboxContext?.messageId || "";
      const requestUserEmail = metadata.userEmail || mailboxContext?.userEmail || "";

      if (!requestMessageId) {
        throw new Error("Unable to identify this Outlook message. Please reopen the email and try again.");
      }

      const emailBody = await readOutlookBody(item);
      if (!emailBody.trim()) {
        throw new Error("The selected email has no readable body.");
      }

      // If "Analyse Attachments" is enabled, fetch attachment contents from Outlook
      let attachmentPayloads = [];
      if (analyseAttachments) {
        try {
          if (item && item.attachments && item.attachments.length > 0) {
            attachmentPayloads = await Promise.all(
              item.attachments.map(async (att) => {
                try {
                  const content = await new Promise((resolve) => {
                    item.getAttachmentContentAsync(att.id, (result) => {
                      if (result?.status === Office.AsyncResultStatus?.Succeeded) {
                        resolve(result.value?.content || "");
                      } else {
                        resolve("");
                      }
                    });
                  });
                  return {
                    id: att.id,
                    name: att.name,
                    contentType: att.contentType,
                    size: att.size,
                    attachmentType: att.attachmentType,
                    content,
                  };
                } catch {
                  return null;
                }
              })
            );
            attachmentPayloads = attachmentPayloads.filter((a) => a && a.content);
          }
        } catch (attErr) {
          console.error("Failed to read attachments for email reply:", attErr);
        }
      }

      const response = await apiService.generateEmailReply({
        messageId: requestMessageId,
        userEmail: requestUserEmail,
        subject: item.subject || "",
        senderName: item.from?.displayName || "",
        emailBody,
        responseInput,
        forceRegenerate: Boolean(draftHtml),
        attachments: attachmentPayloads.length ? JSON.stringify(attachmentPayloads) : "",
      });

      if (currentMessageIdRef.current && currentMessageIdRef.current !== requestMessageId) {
        return;
      }

      setDraftHtml(plainTextToHtml(response.draft));
      setDraftStatus(
        response.cached
          ? "Previously generated draft loaded."
          : `Reply draft generated${response.version ? ` (version ${response.version})` : ""}.`
      );
    } catch (generationError) {
      console.error("Standalone email reply generation failed:", generationError);
      if (!requestMessageId || currentMessageIdRef.current === requestMessageId) {
        setError(generationError?.message || "Unable to generate the email reply. Please try again.");
      }
    } finally {
      if (!requestMessageId || currentMessageIdRef.current === requestMessageId) {
        setGenerating(false);
      }
    }
  };

  const handleOpenReply = () => {
    try {
      const item = getCurrentItem();
      if (!item?.displayReplyForm) {
        throw new Error("Outlook reply drafting is not available for this item.");
      }

      item.displayReplyForm({ htmlBody: draftHtml });
    } catch (replyError) {
      console.error("Failed to open Outlook reply:", replyError);
      setError(replyError?.message || "Unable to open an Outlook reply draft.");
    }
  };

  const handleCopy = async () => {
    try {
      setError("");
      await copyTextToClipboard(htmlToPlainText(draftHtml));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (copyError) {
      console.error("Failed to copy email reply:", copyError);
      setError("Unable to copy the email reply. Please select and copy it manually.");
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <section className="email-reply-card" aria-label="Draft Email Reply">
      <div className="email-reply-card-header">
        <div>
          <h3>Draft Email Reply</h3>
          <p>Create a professional reply without creating a case.</p>
        </div>
        <button
          type="button"
          className="email-reply-close-button"
          onClick={() => setOpen(false)}
          aria-label="Close email reply draft"
          title="Close"
        >
          ×
        </button>
      </div>

      <label className="email-reply-input-label" htmlFor="email-reply-response-input">
        Input for response <span>(optional)</span>
      </label>
      <textarea
        id="email-reply-response-input"
        className="email-reply-input"
        value={responseInput}
        onChange={(event) => setResponseInput(event.target.value)}
        maxLength={MAX_RESPONSE_INPUT_LENGTH}
        disabled={generating}
        placeholder="Example: Thank them for the update and ask them to confirm a convenient time to discuss it."
      />
      <div className="email-reply-input-meta">
        <span>Use this to guide the tone or key points of the reply.</span>
        <span>{responseInput.length}/{MAX_RESPONSE_INPUT_LENGTH}</span>
      </div>

      {/* ── Analyse Attachments Toggle inside Draft Card ── */}
      <div className="analyse-attachments-toggle" style={{ margin: "0 0 10px 0" }}>
        <div className="toggle-left">
          <span className="toggle-icon">📎</span>
          <div className="toggle-text">
            <span className="toggle-label">Analyse Attachments</span>
            <span className="toggle-desc">
              {analyseAttachments
                ? "Attachments will be included in email reply draft"
                : "Enable to include attachments in email reply draft"}
            </span>
          </div>
        </div>
        <button
          type="button"
          className={`toggle-switch ${analyseAttachments ? "toggle-on" : ""}`}
          onClick={() => onToggleAnalyseAttachments?.(!analyseAttachments)}
          role="switch"
          aria-checked={analyseAttachments}
          aria-label="Analyse Attachments"
          disabled={generating}
        >
          <span className="toggle-knob" />
        </button>
      </div>

      {error && <div className="email-reply-error">{error}</div>}

      <button
        type="button"
        className="email-reply-generate-button"
        onClick={handleGenerate}
        disabled={generating}
      >
        {generating ? <span className="btn-spinner" /> : null}
        {generating
          ? "Generating Reply..."
          : draftHtml
          ? "🔄 Regenerate Email Reply"
          : "Generate Email Reply"}
      </button>

      {draftHtml && (
        <div className="email-reply-preview">
          <div className="email-reply-preview-header">
            <span>Email Reply Draft</span>
          </div>
          <TiptapEditor content={draftHtml} onChange={setDraftHtml} />
          <div className="email-reply-actions">
            <button type="button" className="email-reply-open-button" onClick={handleOpenReply}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              Open Reply
            </button>
            <button
              type="button"
              className={`email-reply-secondary-button ${copied ? "copied" : ""}`}
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default EmailReplyDraft;
